// B-176 — the guided tours of the Cierres screens and of every dialog they open.
//
// A step is written on the element it explains: `{...guia("Filtros", "Elige el periodo…")}` puts
// its title and its sentence in `data-guia-titulo` / `data-guia`. A tour is then every marked
// element inside one place — the page (the header and `<main>`) or one dialog — in reading order,
// opened by a step about the place itself. So what a screen shows is what its tour explains: a
// button the person's permissions hide, a feature the client does not have or a table a phone
// draws as cards is simply not in it, and a new element is explained by marking it, nowhere else.

import { explicacionDe } from "./cierreGuiaDashboard";

/** The attributes that make an element a step of its tour. */
export function guia(titulo, texto) {
  return { "data-guia-titulo": titulo, "data-guia": texto };
}

/**
 * The same, for a dashboard card: its title, and what it means said precisely
 * (`cierreGuiaDashboard.js`), never just the subtitle already on the card — that is only the
 * fallback for a card the dictionary does not know. Nothing when the title is not text.
 */
export function guiaDe(titulo, texto) {
  if (typeof titulo !== "string" || !titulo) return {};
  return guia(titulo, explicacionDe(titulo) || (typeof texto === "string" ? texto : ""));
}

/** Whether an element is drawn: in the document, with a box, not inside a hidden block. */
export function estaVisible(el) {
  if (!el || !el.isConnected) return false;
  if (el.closest("[hidden]")) return false;
  const caja = el.getBoundingClientRect();
  return caja.width > 0 && caja.height > 0;
}

const DIALOGO = '[role="dialog"], [role="alertdialog"]';

/**
 * Where a tour reads its steps: the dialog the "?" sits in, or the whole page without its dialogs
 * (a dialog is drawn outside the page, and has its own "?").
 */
export function lugarDeGuia(boton) {
  return (boton && boton.closest(DIALOGO)) || (typeof document !== "undefined" ? document : null);
}

/**
 * The steps of a tour: an opening one about the place (`inicio`, no element, shown in the middle
 * of the screen), then every drawn element marked with `guia()` inside `raiz`, in document order.
 * On the page, a marked element inside a dialog is left out; inside a marked element, a second one
 * marked the same is too (a list and its rows would otherwise say the same thing twice).
 */
export function pasosDeGuia(raiz, inicio) {
  const pasos = inicio ? [{ titulo: inicio.titulo, texto: inicio.texto, elemento: null }] : [];
  if (!raiz) return pasos;
  const enDialogo = raiz.matches && raiz.matches(DIALOGO);
  const vistos = new Set();
  [...raiz.querySelectorAll("[data-guia]")].forEach((el) => {
    if (!enDialogo && el.closest(DIALOGO)) return;
    if (!estaVisible(el)) return;
    const titulo = el.getAttribute("data-guia-titulo") || "";
    const texto = el.getAttribute("data-guia") || "";
    const clave = `${titulo}\u0000${texto}`;
    if (vistos.has(clave)) return;
    vistos.add(clave);
    pasos.push({ titulo, texto, elemento: el });
  });
  return pasos;
}

const clave = (usuario, guiaId) => `cierres_guia_${usuario || "anonimo"}_${guiaId}`;

/** Whether this person already went through this tour (in this browser). */
export function guiaVista(usuario, guiaId) {
  try {
    return window.localStorage.getItem(clave(usuario, guiaId)) === "1";
  } catch (e) {
    // Storage blocked (a private window): the "?" keeps its pulse, nothing else changes.
    return false;
  }
}

/** Remembers that this person went through this tour. */
export function marcarGuiaVista(usuario, guiaId) {
  try {
    window.localStorage.setItem(clave(usuario, guiaId), "1");
  } catch (e) {
    // Nothing to do: the tour still ran.
  }
}

/** What each Cierres page's tour opens with, by its path. */
export const INICIOS_DE_PAGINA = {
  "/business/backoffice/panel-cierres": {
    nombre: "Panel",
    titulo: "Panel de cierres",
    texto: "Aquí ves el cuadre de cada oficina, día por día. Te mostramos para qué sirve cada parte.",
  },
  "/business/backoffice/calendario": {
    nombre: "Calendario",
    titulo: "Calendario de cierres",
    texto: "El mes de cada oficina de un vistazo: qué días cuadraron, cuáles tienen alerta y cuáles faltan.",
  },
  "/business/backoffice/reportes": {
    nombre: "Reportes",
    titulo: "Reportes",
    texto: "El dashboard del cliente: cómo va el cuadre, dónde están las diferencias y cómo cambia en el tiempo.",
  },
  "/business/backoffice/oficinas": {
    nombre: "Oficinas",
    titulo: "Oficinas",
    texto: "El directorio de oficinas del cliente con el estado de su último cierre.",
  },
  "/business/backoffice/chat": {
    nombre: "Asistente",
    titulo: "Asistente",
    texto: "Pregúntale en tus palabras por cierres, diferencias u oficinas y te responde con los datos del cliente que estás viendo.",
  },
  "/business/backoffice/alertas": {
    nombre: "Alertas",
    titulo: "Alertas",
    texto: "Las diferencias que hay que revisar, día por día, y las que ya quedaron atendidas.",
  },
  "/business/backoffice/usuarios": {
    nombre: "Usuarios",
    titulo: "Usuarios",
    texto: "Quién entra a la plataforma y qué puede ver y hacer cada persona.",
  },
};
